import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { PurchaseOrderRecord, PoActionType } from '@/types/domain/po-management';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle, CheckCircle, RotateCcw, XCircle, Send, Check, RefreshCw, X } from 'lucide-react';

interface PoActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionType: PoActionType | null;
  po: PurchaseOrderRecord | null;
  onConfirm: (payload: { reason?: string; notes?: string }) => Promise<void>;
}

export const PoActionModal: React.FC<PoActionModalProps> = ({
  isOpen,
  onClose,
  actionType,
  po,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen || !actionType || !po) return null;

  const isDestructive = actionType === 'CANCEL' || actionType === 'REJECT';
  const requiresReason =
    actionType === 'CANCEL' ||
    actionType === 'REJECT' ||
    actionType === 'SEND_BACK' ||
    actionType === 'AMEND';

  const getActionDetails = () => {
    switch (actionType) {
      case 'SUBMIT':
        return {
          title: `Submit PO ${po.poNumber} for Authorization`,
          desc: 'This order will enter the multi-tier approval workflow based on total commercial value.',
          confirmText: 'Confirm Submission',
          icon: <Send className="h-5 w-5 text-primary" />,
          placeholder: 'Optional submission note for approvers...',
        };
      case 'APPROVE':
        return {
          title: `Authorize & Release PO ${po.poNumber}`,
          desc: `You are approving a commercial commitment of ${formatCurrency(po.totalValue, po.currency)} with ${po.vendorName}.`,
          confirmText: 'Authorize & Release',
          icon: <CheckCircle className="h-5 w-5 text-emerald-600" />,
          placeholder: 'Optional approval comments...',
        };
      case 'REJECT':
        return {
          title: `Reject Purchase Order ${po.poNumber}`,
          desc: 'Rejecting this purchase order will terminate the procurement workflow and cancel committed line items.',
          confirmText: 'Confirm Rejection',
          icon: <XCircle className="h-5 w-5 text-rose-600" />,
          placeholder: 'Mandatory reason for rejection (required for audit)...',
        };
      case 'SEND_BACK':
        return {
          title: `Send Back PO ${po.poNumber} for Revision`,
          desc: 'Return this purchase order to the buyer for specification or pricing modifications.',
          confirmText: 'Send Back to Buyer',
          icon: <RotateCcw className="h-5 w-5 text-amber-600" />,
          placeholder: 'Specify what revisions are required by the buyer...',
        };
      case 'ISSUE':
        return {
          title: `Send PO ${po.poNumber} to Supplier`,
          desc: `Transmits the finalized order and legal terms to ${po.vendorName} via EDI / Electronic Dispatch.`,
          confirmText: 'Transmit Order',
          icon: <Send className="h-5 w-5 text-blue-600" />,
          placeholder: 'Optional transmission instructions...',
        };
      case 'ACKNOWLEDGE':
        return {
          title: `Record Supplier Acknowledgment`,
          desc: `Confirm that ${po.vendorName} has accepted PO ${po.poNumber} and confirmed the delivery schedule.`,
          confirmText: 'Record Acknowledgment',
          icon: <Check className="h-5 w-5 text-emerald-600" />,
          placeholder: 'Supplier confirmation notes or reference number...',
        };
      case 'CANCEL':
        return {
          title: `Cancel Purchase Order ${po.poNumber}`,
          desc: `Are you sure you want to cancel this order? This cancels the commercial commitment of ${formatCurrency(po.totalValue, po.currency)}.`,
          confirmText: 'Confirm Cancellation',
          icon: <AlertTriangle className="h-5 w-5 text-rose-600" />,
          placeholder: 'Mandatory cancellation reason (required for compliance)...',
        };
      case 'CLOSE':
        return {
          title: `Close Purchase Order ${po.poNumber}`,
          desc: 'Closing the PO indicates all goods have been received, invoices reconciled, and commitments closed.',
          confirmText: 'Close Order',
          icon: <CheckCircle className="h-5 w-5 text-slate-600" />,
          placeholder: 'Final closure and reconciliation notes...',
        };
      case 'AMEND':
        return {
          title: `Amend Purchase Order ${po.poNumber}`,
          desc: 'Create an official revision amendment. A new revision number will be assigned in audit history.',
          confirmText: 'Create Amendment',
          icon: <RefreshCw className="h-5 w-5 text-purple-600" />,
          placeholder: 'Detailed justification for PO amendment...',
        };
      default:
        return {
          title: `Confirm Action on ${po.poNumber}`,
          desc: 'Please confirm this action.',
          confirmText: 'Confirm',
          icon: <AlertTriangle className="h-5 w-5 text-primary" />,
          placeholder: 'Notes...',
        };
    }
  };

  const details = getActionDetails();

  const handleConfirm = async () => {
    if (requiresReason && !reason.trim()) {
      setError('A reason is mandatory for this action.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onConfirm({ reason: reason.trim(), notes: reason.trim() });
      setReason('');
      onClose();
    } catch (err: unknown) {
      setError((err as Error).message || 'Operation failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-lg rounded-2xl p-6 bg-card border border-border shadow-2xl z-10 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${isDestructive ? 'bg-rose-500/10' : 'bg-primary/10'}`}>
              {details.icon}
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-foreground">{details.title}</h3>
              <p className="text-xs text-foreground-secondary leading-relaxed">{details.desc}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-foreground-muted hover:text-foreground p-1 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* PO Commercial Commitment Brief */}
        <div className="p-3.5 rounded-xl border border-border/70 bg-surface/50 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-secondary font-medium">Selected Supplier:</span>
            <span className="font-extrabold text-foreground">{po.vendorName} ({po.vendorCode})</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-secondary font-medium">Total Committed Value:</span>
            <span className="font-extrabold text-primary">{formatCurrency(po.totalValue, po.currency)}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-secondary font-medium">Line Items Count:</span>
            <span className="font-semibold text-foreground">{po.items.length} items</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-secondary font-medium">Current Status:</span>
            <span className="font-bold text-foreground uppercase text-[11px]">{po.status}</span>
          </div>
        </div>

        {/* Reason / Comments Input */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-foreground flex items-center justify-between">
            <span>{requiresReason ? 'Justification / Reason (Mandatory)' : 'Comments / Notes (Optional)'}</span>
            {requiresReason && <span className="text-[10px] text-rose-500 font-bold">*Required</span>}
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder={details.placeholder}
            rows={3}
            className="w-full text-xs font-medium p-3 rounded-xl border border-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
          {error && <p className="text-[11px] font-bold text-rose-600">{error}</p>}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2.5 pt-2 border-t border-border/40">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl font-bold border-border"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={`rounded-xl font-bold ${
              isDestructive
                ? 'bg-rose-600 hover:bg-rose-700 text-white'
                : 'bg-primary hover:bg-primary/90 text-white'
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Processing...
              </span>
            ) : (
              details.confirmText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
